import { StyleSheet, Text, View } from 'react-native'
import { formatGBP } from '../format'
import { colors, fontFamily, fontSize, radius, space } from '../tokens'
import { verdictTier } from '../verdict-tier'
import { BandCheckVerdict } from './BandCheckVerdict'

type Refund = {
  bestGuess: number
  rangeLow: number
  rangeHigh: number
  yearsApplied: number | null
}

type Props = {
  addressLine1: string
  postcode: string
  localAuthority: string
  currentBand: string
  suggestedBand: string
  confidence: number
  flaggedAsOverbanded: boolean
  totalScore?: number
  caseLabel?: 'Good case' | 'Strong case' | 'Excellent case' | null
  includedCount: number
  subjectAttributesSource?: 'epc' | 'none'
  estimatedAnnualSaving: number
  refund: Refund
}

const refundLine = (refund: Refund) => {
  if (refund.bestGuess > 0 && refund.yearsApplied != null) {
    const years = Math.round(refund.yearsApplied * 10) / 10
    return `Plus an estimated ${formatGBP(refund.bestGuess)} backdated to your move-in (${years} years).`
  }
  if (refund.rangeHigh > refund.rangeLow) {
    return `Plus an estimated ${formatGBP(refund.rangeLow)}–${formatGBP(refund.rangeHigh)} backdated to your move-in (depending on how long you've owned).`
  }
  return ''
}

export const BandCheckResultHeader = ({
  addressLine1,
  postcode,
  localAuthority,
  currentBand,
  suggestedBand,
  confidence,
  flaggedAsOverbanded,
  totalScore,
  caseLabel,
  includedCount,
  subjectAttributesSource,
  estimatedAnnualSaving,
  refund,
}: Props) => {
  const tier = verdictTier(flaggedAsOverbanded, totalScore, confidence)
  const showSuggested = flaggedAsOverbanded
  return (
    <>
      <Text style={styles.address}>{addressLine1}</Text>
      <Text style={styles.subAddress}>
        {postcode} · {localAuthority}
      </Text>

      <BandCheckVerdict
        tier={tier}
        caseLabel={caseLabel}
        flagged={flaggedAsOverbanded}
        includedCount={includedCount}
        subjectSource={subjectAttributesSource}
      />

      <View style={styles.bandRow}>
        <View style={styles.bandBox}>
          <Text style={styles.bandLabel}>Current band</Text>
          <Text style={styles.bandValue}>{currentBand}</Text>
        </View>
        <Text style={styles.bandArrow} accessibilityElementsHidden>
          →
        </Text>
        <View style={[styles.bandBox, showSuggested && styles.bandBoxSuggest]}>
          <Text style={styles.bandLabel}>{showSuggested ? 'Suggested band' : 'Confirmed'}</Text>
          <Text style={[styles.bandValue, showSuggested && styles.bandValueSuggest]}>
            {showSuggested ? suggestedBand : currentBand}
          </Text>
        </View>
      </View>

      {flaggedAsOverbanded && estimatedAnnualSaving > 0 ? (
        <View style={styles.savingBox}>
          <View style={styles.savingHead}>
            <Text style={styles.savingAmount}>{formatGBP(estimatedAnnualSaving)}</Text>
            <Text style={styles.savingLabel}>estimated saving / year</Text>
          </View>
          {refundLine(refund) ? <Text style={styles.refund}>{refundLine(refund)}</Text> : null}
        </View>
      ) : null}
    </>
  )
}

const styles = StyleSheet.create({
  address: {
    fontSize: 30,
    fontWeight: '500',
    color: colors.ink,
    fontFamily: fontFamily.serif,
    letterSpacing: -0.4,
  },
  subAddress: {
    fontSize: fontSize.small,
    color: colors.muted,
    marginBottom: space[16],
    fontFamily: fontFamily.body,
  },
  bandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: space[24],
  },
  bandBox: {
    flex: 1,
    padding: 18,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface2,
    alignItems: 'center',
  },
  bandBoxSuggest: {
    borderColor: colors.positive,
    backgroundColor: colors.positiveSoft,
  },
  bandLabel: {
    fontSize: 11.5,
    fontWeight: '600',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.muted,
    marginBottom: space[8],
    fontFamily: fontFamily.body,
  },
  bandValue: {
    fontSize: 52,
    fontWeight: '500',
    lineHeight: 52,
    letterSpacing: -1,
    color: colors.ink,
    fontFamily: fontFamily.serif,
  },
  bandValueSuggest: {
    color: colors.positiveInk,
  },
  bandArrow: {
    color: colors.faint,
    fontSize: 22,
    fontWeight: '600',
  },
  savingBox: {
    padding: 20,
    borderRadius: radius.sm,
    backgroundColor: colors.ink,
    marginBottom: space[24],
  },
  savingHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 10,
  },
  savingAmount: {
    fontSize: 40,
    fontWeight: '500',
    color: '#FFFFFF',
    letterSpacing: -0.6,
    fontFamily: fontFamily.serif,
  },
  savingLabel: {
    fontSize: 14,
    color: '#FFFFFF',
    opacity: 0.8,
    fontFamily: fontFamily.body,
  },
  refund: {
    fontSize: 13,
    color: '#FFFFFF',
    opacity: 0.7,
    marginTop: space[8],
    lineHeight: 19,
    fontFamily: fontFamily.body,
  },
})
